import React, { useState, useEffect, useMemo } from "react";
import {
  GoServer,
  GoSearch,
  GoSync,
  GoFileDirectory,
} from "react-icons/go";
import { BiData } from "react-icons/bi";
import { HiOutlineDocumentText } from "react-icons/hi";
import { FiAlertTriangle, FiCheckCircle } from "react-icons/fi";

import { Container, IndexCardSkeleton } from "./styles";
import elasticsearchService from "../../services/elasticsearchService";
import IndexSummaryModal from "./IndexSummaryModal";
import IndexDetailsModal from "./IndexDetailsModal";
import ConfirmModal from "../../components/ConfirmModal";

/* ============================================================
 * Helpers
 * ============================================================ */
function parseSizeToBytes(sizeStr) {
  const s = (sizeStr || "0b").toLowerCase();
  if (s.includes("gb")) return parseFloat(s) * 1024 * 1024 * 1024;
  if (s.includes("mb")) return parseFloat(s) * 1024 * 1024;
  if (s.includes("kb")) return parseFloat(s) * 1024;
  if (s.includes("b"))  return parseFloat(s);
  return 0;
}

function fmtAgo(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  const diff = Date.now() - d.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days < 1) {
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 1) return "agora";
    return `${hours}h`;
  }
  if (days < 30)   return `${days}d`;
  if (days < 365)  return `${Math.floor(days / 30)}mo`;
  return `${Math.floor(days / 365)}y`;
}

/* ============================================================
 * COMPONENT
 * ============================================================ */
export default function Admin() {
  const [indices, setIndices] = useState([]);
  const [backups, setBackups] = useState([]);
  const [clusterHealth, setClusterHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | archived
  const [archiving, setArchiving] = useState(false);

  // Modal state
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [indexStats, setIndexStats] = useState({});

  // Confirm modal
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    warning: "",
    onConfirm: null,
    variant: "info",
    confirmText: "Confirmar",
    confirmVariant: "primary",
  });

  /* ============ FETCH ============ */
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [indicesResponse, backupsResponse, healthResponse] = await Promise.all([
        elasticsearchService.listIndices(),
        elasticsearchService.listBackups(),
        elasticsearchService.getClusterHealth(),
      ]);

      if (indicesResponse.success)  setIndices(indicesResponse.data);
      if (backupsResponse.success)  setBackups(backupsResponse.data);
      if (healthResponse.success)   setClusterHealth(healthResponse.data);
    } catch (err) {
      console.error("Erro ao carregar dados:", err);
      setError("Erro ao conectar com o Elasticsearch. Verifique se o serviço está rodando.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  /* ============ WS auto-refresh quando arquive/restore completa ============ */
  useEffect(() => {
    const websocketTaskService = require("../../services/websocketTaskService").default;
    websocketTaskService.connect();

    const unsubscribe = websocketTaskService.subscribe((tasks) => {
      const hasCompletedRelevantTask = tasks.some(
        (t) =>
          (t.type === "archive_index" || t.type === "restore_index") &&
          t.status === "completed"
      );
      if (hasCompletedRelevantTask && !loading) {
        fetchData();
      }
    });

    return () => unsubscribe();
  }, [loading]);

  /* ============ LOAD DETAILS ON DEMAND ============ */
  const loadIndexDetails = async (indexName) => {
    try {
      const [detailsResponse, sampleResponse] = await Promise.all([
        elasticsearchService.getIndexDetails(indexName),
        elasticsearchService.getSampleDocument(indexName),
      ]);

      if (detailsResponse.success) {
        const mappings = detailsResponse.data.mappings?.properties || {};
        const sampleDoc =
          sampleResponse.success && sampleResponse.data
            ? sampleResponse.data._source
            : null;

        setIndexStats((prev) => ({
          ...prev,
          [indexName]: {
            mappings: { properties: mappings },
            sampleDoc,
          },
        }));
      }
    } catch (err) {
      console.error(`Erro ao carregar detalhes do índice ${indexName}:`, err);
    }
  };

  /* ============ HANDLERS ============ */
  const handleOpenSummary = async (index) => {
    setSelectedIndex(index);
    setSummaryOpen(true);
    if (!index.isArchived && !indexStats[index.name]) {
      await loadIndexDetails(index.name);
    }
  };

  const handleOpenDocs = (index) => {
    setSelectedIndex(index);
    setSummaryOpen(false);
    setDocsOpen(true);
  };

  const handleCloseModals = () => {
    setSummaryOpen(false);
    setDocsOpen(false);
    setSelectedIndex(null);
  };

  const handleArchiveIndex = (indexName) => {
    setConfirmModal({
      isOpen: true,
      title: "Arquivar Índice",
      message: `Deseja arquivar o índice "${indexName}"? Isso criará um backup comprimido em RAVENA-BACK/backups.`,
      warning:
        "ATENÇÃO: O índice original será DELETADO após o backup ser criado com sucesso. Esta ação não pode ser desfeita.",
      variant: "danger",
      confirmText: "Sim, Arquivar",
      confirmVariant: "danger",
      onConfirm: async () => {
        setConfirmModal((m) => ({ ...m, isOpen: false }));
        try {
          setArchiving(true);
          const response = await elasticsearchService.archiveIndex(indexName, true);
          if (response.success) {
            const websocketTaskService = require("../../services/websocketTaskService").default;
            websocketTaskService.addTask({
              operationId: response.operationId,
              type: "archive_index",
              indexName: indexName,
              message: "Starting archive request...",
            });
          }
          handleCloseModals();
        } catch (err) {
          console.error("Erro ao arquivar índice:", err);
          setConfirmModal({
            isOpen: true,
            title: "Erro ao Arquivar",
            message: err.response?.data?.error || err.message,
            variant: "danger",
            confirmText: "OK",
            confirmVariant: "primary",
            onConfirm: () => setConfirmModal((m) => ({ ...m, isOpen: false })),
          });
        } finally {
          setArchiving(false);
        }
      },
    });
  };

  const handleRestoreBackup = (backupId, indexName) => {
    setConfirmModal({
      isOpen: true,
      title: "Restaurar Backup",
      message: `Deseja restaurar o backup do índice "${indexName}"? O índice será recriado no Elasticsearch com todos os documentos arquivados.`,
      warning: "O backup será deletado após a restauração bem-sucedida.",
      variant: "warning",
      confirmText: "Sim, Restaurar",
      confirmVariant: "primary",
      onConfirm: async () => {
        setConfirmModal((m) => ({ ...m, isOpen: false }));
        try {
          const response = await elasticsearchService.restoreBackup(backupId);
          if (response.success) fetchData();
          handleCloseModals();
        } catch (err) {
          const msg = err.response?.data?.error || err.message;
          setConfirmModal({
            isOpen: true,
            title: "Erro ao Restaurar",
            message: msg,
            variant: "danger",
            confirmText: "OK",
            confirmVariant: "primary",
            onConfirm: () => setConfirmModal((m) => ({ ...m, isOpen: false })),
          });
        }
      },
    });
  };

  /* ============ DERIVED DATA ============ */
  const archivedIndices = useMemo(
    () =>
      backups.map((backup) => ({
        name: backup.indexName,
        health: "archived",
        status: "archived",
        docsCount: backup.documentCount,
        docsDeleted: 0,
        storeSize: backup.sizeBeforeFormatted || "0b",
        snapshotSize: backup.sizeAfterFormatted || "0b",
        compressionRatio: backup.compressionRatio || "0%",
        priShards: backup.thumbnail?.priShards || 0,
        replicas: backup.thumbnail?.replicas || 0,
        refDate: backup.archivedAt,
        isArchived: true,
        backupId: backup.id,
        backupFilename: backup.foldername,
        mappings: backup.mappings,
        sampleDocument: backup.sampleDocument,
      })),
    [backups]
  );

  const allIndices = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const activeList = indices.filter((i) => i.name.toLowerCase().includes(term));
    const archivedList = archivedIndices.filter((i) => i.name.toLowerCase().includes(term));

    if (statusFilter === "active")   return activeList;
    if (statusFilter === "archived") return archivedList;
    return [...activeList, ...archivedList];
  }, [indices, archivedIndices, searchTerm, statusFilter]);

  const totalDocs = useMemo(
    () => indices.reduce((sum, idx) => sum + (idx.docsCount || 0), 0),
    [indices]
  );

  const totalSizeBytes = useMemo(
    () => indices.reduce((sum, idx) => sum + parseSizeToBytes(idx.storeSize), 0),
    [indices]
  );
  const totalSizeGB = totalSizeBytes / (1024 * 1024 * 1024);

  const healthyCount  = indices.filter((i) => i.health === "green").length;
  const yellowCount   = indices.filter((i) => i.health === "yellow").length;
  const clusterStatus = clusterHealth?.status || "loading";

  /* ============ RENDER ============ */
  return (
    <Container>
      {/* HEADER */}
      <div className="header-info">
        <div className="header-details">
          <h3>Administração Elasticsearch</h3>
          <div className="header-meta">
            <span className={`status-tag ${clusterStatus}`}>
              CLUSTER {clusterStatus.toUpperCase()}
            </span>
            <span className="update-tag">
              {indices.length} índices · {archivedIndices.length} arquivados
            </span>
          </div>
        </div>
      </div>

      {/* KPI CARDS */}
      <div className="kpi-cards">
        <div className="kpi-card">
          <div className="card-content">
            <div className="thumb"><GoServer /></div>
            <div className="text">
              <p>{indices.length}</p>
              <p>Índices Totais</p>
            </div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="card-content">
            <div className="thumb"><HiOutlineDocumentText /></div>
            <div className="text">
              <p>{totalDocs.toLocaleString("pt-BR")}</p>
              <p>Documentos</p>
            </div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="card-content">
            <div className="thumb"><BiData /></div>
            <div className="text">
              <p>{totalSizeGB.toFixed(2)} GB</p>
              <p>Tamanho Total</p>
            </div>
          </div>
        </div>

        <div className="kpi-card success">
          <div className="card-content">
            <div className="thumb"><FiCheckCircle /></div>
            <div className="text">
              <p>{healthyCount}</p>
              <p>Saudáveis</p>
            </div>
          </div>
        </div>

        <div className={`kpi-card ${yellowCount > 0 ? "warning" : ""}`}>
          <div className="card-content">
            <div className="thumb"><FiAlertTriangle /></div>
            <div className="text">
              <p>{yellowCount}</p>
              <p>Avisos</p>
            </div>
          </div>
        </div>
      </div>

      {/* TOOLBAR */}
      <div className="toolbar">
        <div className="search-box">
          <GoSearch className="search-icon" />
          <input
            type="text"
            placeholder="Buscar índices..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-pills">
          <button
            className={`filter-pill ${statusFilter === "all" ? "active" : ""}`}
            onClick={() => setStatusFilter("all")}
          >
            Todos
          </button>
          <button
            className={`filter-pill ${statusFilter === "active" ? "active" : ""}`}
            onClick={() => setStatusFilter("active")}
          >
            Ativos
          </button>
          <button
            className={`filter-pill ${statusFilter === "archived" ? "active" : ""}`}
            onClick={() => setStatusFilter("archived")}
          >
            Arquivados
          </button>
        </div>

        <button
          className="action-btn primary"
          onClick={fetchData}
          disabled={loading}
        >
          <GoSync className={loading ? "spin" : ""} />
          {loading ? "Atualizando..." : "Atualizar"}
        </button>
      </div>

      {/* ERROR */}
      {error && (
        <div className="error-message">
          <FiAlertTriangle />
          <span>{error}</span>
        </div>
      )}

      {/* INDICES GRID */}
      <div className="indices-grid">
        {loading ? (
          <>
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <IndexCardSkeleton key={i}>
                <div className="sk-header">
                  <div className="sk sk-name" />
                  <div className="sk sk-badge" />
                </div>
                <div className="sk-stats">
                  <div className="sk sk-value" />
                  <div className="sk sk-value" />
                </div>
                <div className="sk sk-footer" />
              </IndexCardSkeleton>
            ))}
          </>
        ) : allIndices.length === 0 ? (
          <div className="empty-state">
            <GoFileDirectory />
            <p>
              {searchTerm
                ? "Nenhum índice encontrado para esta busca"
                : "Nenhum índice encontrado"}
            </p>
          </div>
        ) : (
          allIndices.map((index) => {
            const cardKey = index.isArchived
              ? `archived-${index.backupId}`
              : index.name;
            const healthClass = index.isArchived ? "archived" : index.health;
            const subLabel = index.isArchived
              ? `arquivado · ${fmtAgo(index.refDate) || ""}`
              : `vidal${index.refDate ? ` · ${fmtAgo(index.refDate)}` : ""}`;

            return (
              <div
                key={cardKey}
                className={`index-card-new ${index.isArchived ? "archived" : ""}`}
                onClick={() => handleOpenSummary(index)}
              >
                {/* Header */}
                <div className="card-header">
                  <div className="index-info">
                    <span className="index-name">{index.name}</span>
                    <span className="index-sub">{subLabel}</span>
                  </div>
                  <div className={`index-health ${healthClass}`}>
                    <span className="health-dot" />
                    {index.isArchived ? "arquivado" : index.health}
                  </div>
                </div>

                {/* Hero stats */}
                <div className="hero-stats">
                  <div className="hero-stat">
                    <span className="hero-value">
                      {(index.docsCount || 0).toLocaleString("pt-BR")}
                    </span>
                    <span className="hero-label">Documentos</span>
                  </div>
                  <div className="hero-stat">
                    <span className="hero-value">{index.storeSize || "—"}</span>
                    <span className="hero-label">
                      {index.isArchived ? "Tamanho Orig" : "Tamanho"}
                    </span>
                  </div>
                </div>

                {/* Micro stats inline */}
                <div className="micro-stats-inline">
                  <span className="micro-inline">
                    <span className="micro-key">Shards</span>
                    <span className="micro-val">{index.priShards ?? 0}</span>
                  </span>
                  <span className="micro-inline">
                    <span className="micro-key">Repl</span>
                    <span className="micro-val">{index.replicas ?? 0}</span>
                  </span>
                  {index.isArchived && index.compressionRatio && (
                    <span className="micro-inline">
                      <span className="micro-key">Compr</span>
                      <span className="micro-val">{index.compressionRatio}</span>
                    </span>
                  )}
                  <span
                    className={`status-pill ${
                      index.status === "open"
                        ? "open"
                        : index.status === "close"
                        ? "close"
                        : ""
                    }`}
                  >
                    {index.status}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* SUMMARY MODAL (640px) */}
      {summaryOpen && selectedIndex && (
        <IndexSummaryModal
          index={selectedIndex}
          stats={indexStats[selectedIndex.name]}
          archiving={archiving}
          onClose={handleCloseModals}
          onOpenDocs={() => handleOpenDocs(selectedIndex)}
          onArchive={() => handleArchiveIndex(selectedIndex.name)}
          onRestore={() =>
            handleRestoreBackup(selectedIndex.backupId, selectedIndex.name)
          }
        />
      )}

      {/* DOCS MODAL (1100px) */}
      {docsOpen && selectedIndex && (
        <IndexDetailsModal
          index={selectedIndex}
          onClose={handleCloseModals}
        />
      )}

      {/* CONFIRM MODAL */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() =>
          setConfirmModal((m) => ({ ...m, isOpen: false }))
        }
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        warning={confirmModal.warning}
        variant={confirmModal.variant}
        confirmText={confirmModal.confirmText}
        confirmVariant={confirmModal.confirmVariant}
      />
    </Container>
  );
}
